import React, { useState } from "react";
import { Store } from "lucide-react";
import HotspotPlan from "./HotspotPlan";
import EnquiryDialog from "./EnquiryDialog";

const C = { available: "#6E8FD6", pending: "#2E3A4E", negotiation: "#5BAE88", residential: "#9E7A96" };

// Unit positions read from the West18 / Avison Young leasing site plan (page 2).
const UNITS = [
  { id: "box", label: "BOX", x: 0.32, y: 0.68, color: C.pending, name: "Box", size: "28,000 sq ft", status: "Pending", leasable: true },
  { id: "midbox", label: "MID", x: 0.74, y: 0.67, color: C.negotiation, name: "Midbox", size: "17,000 sq ft", status: "Under Negotiation", leasable: true },
  { id: "cruA", label: "A", x: 0.53, y: 0.54, color: C.available, name: "CRU A", size: "1,200 – 4,950 sq ft", status: "Available", leasable: true },
  { id: "cruB", label: "B", x: 0.72, y: 0.56, color: C.available, name: "CRU B", size: "1,200 – 6,693 sq ft", status: "Available", leasable: true },
  { id: "res1", label: "R", x: 0.26, y: 0.42, color: C.residential, name: "4-Storey Residential", size: "Future phase", status: "Residential", leasable: false },
  { id: "res2", label: "R", x: 0.63, y: 0.39, color: C.residential, name: "4-Storey Residential", size: "Future phase", status: "Residential", leasable: false },
];

const LEGEND = [["Available", C.available], ["Under Negotiation", C.negotiation], ["Pending", C.pending], ["Residential", C.residential]];

const CommercialPlan = ({ planImage }) => {
  const [unit, setUnit] = useState(null);
  const pins = UNITS.map((u) => ({ id: u.id, x: u.x, y: u.y, label: u.label, color: u.color, title: `${u.name} — ${u.size} (${u.status})`, unit: u }));

  return (
    <div className="mt-4" data-testid="commercial-plan">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-display text-2xl text-[#F3EEE3] flex items-center gap-2"><Store size={18} className="text-[#C9B79C]" /> Leasing plan — tap a unit to enquire</h2>
        <div className="flex items-center gap-3 flex-wrap text-xs text-[#F3EEE3]/60">
          {LEGEND.map(([l, c]) => (
            <span key={l} className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: c }} /> {l}</span>
          ))}
        </div>
      </div>

      {planImage ? (
        <div className="rounded-2xl overflow-hidden border border-[#F3EEE3]/12 bg-[#0F1B15] p-3 md:p-4">
          <HotspotPlan src={planImage} pins={pins} onPinClick={(pin) => setUnit(pin.unit)} fit />
        </div>
      ) : (
        <div className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-6 text-[#F3EEE3]/50 text-sm">Site plan coming soon.</div>
      )}

      <div className="mt-5 flex flex-wrap gap-2" data-testid="commercial-unit-list">
        {UNITS.map((u) => (
          <button
            key={u.id}
            onClick={() => u.leasable && setUnit(u)}
            disabled={!u.leasable}
            data-testid={`commercial-unit-${u.id}`}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm border text-[#F3EEE3] disabled:opacity-60 disabled:cursor-default enabled:hover:scale-105 transition-transform"
            style={{ borderColor: u.color + "88", background: u.color + "22" }}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: u.color }} />
            {u.name} · {u.size}
          </button>
        ))}
      </div>

      <EnquiryDialog
        open={!!unit}
        onClose={() => setUnit(null)}
        context={unit ? { label: `Commercial — ${unit.name} (${unit.size})`, source: "commercial-lease" } : {}}
      />
    </div>
  );
};

export default CommercialPlan;
